/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\RecordingSystemModeSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Recording Management module.
----------------------------------------------------------------*/

import { useState, useRef } from 'react';
import { LibraryBig, Info, X, Play, Square, Loader2 } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { VoiceGuideSelectionModal } from './VoiceGuideSelectionModal';
import { useI18n } from '../../../hooks/useI18n';
import { RecordingSystemData } from '../types';
import { VoiceGuide } from '../../voiceGuides/types';
import { VoiceGuideAudioFile } from '../../voiceGuides/api/voiceGuidesApi';

interface RecordingSystemModeSectionProps {
  formData: RecordingSystemData;
  setFormData: (data: any) => void;
  errors?: Record<string, string>;
  clearError?: (field: string) => void;
}

export function RecordingSystemModeSection({
  formData,
  setFormData,
  errors,
  clearError,
}: RecordingSystemModeSectionProps) {
  const { t } = useI18n();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeGuide, setActiveGuide] = useState<VoiceGuide | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleSelectVoiceGuide = (guide: VoiceGuide, file: VoiceGuideAudioFile) => {
    setActiveGuide(guide);
    setFormData((prev: any) => ({
      ...prev,
      voice_guide_id: guide.id,
      voice_guide_file_path: file.relativePath,
      file_path: (guide.sound_path ? guide.sound_path.replace(/\/+$/, '') + '/' : '') + file.relativePath,
      name: prev.name || guide.name,
      description: prev.description || guide.description || '',
    }));
    clearError?.('voiceGuideId');
    clearError?.('voiceGuideFilePath');
    clearError?.('name');
    setIsModalOpen(false);
  };

  const clearSelection = () => {
    setActiveGuide(null);
    setFormData((prev: any) => ({
      ...prev,
      voice_guide_id: 0,
      voice_guide_file_path: '',
    }));
  };

  const handlePlayVoiceGuide = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    const targetPath = formData.file_path || formData.voice_guide_file_path;
    if (!targetPath) return;

    try {
      setIsFetching(true);
      const { fetchVoiceGuideStreamBlob } = await import('../api');
      const blob = await fetchVoiceGuideStreamBlob(targetPath);
      const url = URL.createObjectURL(blob);
      
      if (audioRef.current) {
         if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
            URL.revokeObjectURL(audioRef.current.src);
         }
         audioRef.current.src = url;
         await audioRef.current.play();
         setIsPlaying(true);
      }
    } catch (err) {
      console.error('Failed to play voice guide', err);
      setIsPlaying(false);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <FormBox
      title={t('recordingManagement:systemMode.title') || 'Native Voice Guide Mapping'}
      icon={<LibraryBig className="h-4 w-4" />}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('recordingManagement:systemMode.description') || 
            'Link this recording to a specific prompt within a native PBX Voice Guide. This allows you to use existing system sounds across your telephony modules without needing to upload duplicate files.'}
        </p>

        {formData.voice_guide_id && formData.voice_guide_file_path ? (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="bg-muted/30 border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LibraryBig className="h-4 w-4 text-primary" />
                <h3 className="font-medium text-sm text-foreground">
                  {t('recordingManagement:systemMode.selectedFile') || 'Selected Audio File'}
                </h3>
              </div>
              <button
                type="button"
                onClick={clearSelection}
                className="p-1 hover:bg-destructive/10 hover:text-destructive rounded transition-colors"
                title={t('common:remove') || 'Remove selection'}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 grid gap-3">
               <div className="flex justify-between items-center bg-card p-3 rounded-md border border-border shadow-sm">
                 <div>
                   <p className="text-sm font-medium">{activeGuide ? activeGuide.name : 'Selected Voice Guide Pack'}</p>
                   <p className="text-xs text-muted-foreground mt-1 break-all">Path: <code className="bg-muted px-1 py-0.5 rounded text-primary/80">{formData.voice_guide_file_path}</code></p>
                 </div>
                 <div className="flex items-center gap-2">
                   <button
                     type="button"
                     onClick={handlePlayVoiceGuide}
                     disabled={isFetching}
                     className="text-primary p-2 rounded-md hover:bg-primary/10 transition-colors disabled:opacity-50"
                     title={isPlaying ? 'Stop' : 'Play'}
                   >
                     {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : isPlaying ? <Square className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
                   </button>
                   <button
                     type="button"
                     onClick={() => setIsModalOpen(true)}
                     className="text-primary text-sm font-medium bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded transition-colors"
                   >
                     {t('common:change') || 'Change'}
                   </button>
                 </div>
               </div>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center text-center gap-3 hover:border-primary/50 hover:bg-muted/20 transition-all cursor-pointer" onClick={() => setIsModalOpen(true)}>
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-2">
              <LibraryBig className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">
                {t('recordingManagement:systemMode.selectAction') || 'Select a Voice Guide File'}
              </h4>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                {t('recordingManagement:systemMode.selectHint') || 'Browse the system dictionary to choose an existing prompt.'}
              </p>
            </div>
            <button
              type="button"
              className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {t('recordingManagement:systemMode.browseGuides') || 'Browse Voice Guides'}
            </button>
          </div>
        )}

        {(errors?.voiceGuideId || errors?.voiceGuideFilePath) && (
          <p className="text-sm text-destructive mt-1 flex items-center gap-1.5">
            <Info className="h-4 w-4" />
            {errors.voiceGuideId || errors.voiceGuideFilePath}
          </p>
        )}
      </div>

      <VoiceGuideSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleSelectVoiceGuide}
      />
      <audio
        ref={audioRef}
        className="hidden"
        onEnded={() => setIsPlaying(false)}
        onError={() => setIsPlaying(false)}
      />
    </FormBox>
  );
}
